const MESSAGES: Record<string, string> = {
  invalid_credentials: "That email and password don't match.",
  invalid_body: "Some of those details aren't valid.",
  verification_failed: "That passkey wasn't accepted. Try again.",
  unknown_credential: "That passkey isn't registered to this account.",
  no_challenge: "Your sign-in attempt expired. Start again.",
  slug_taken: "That short name is already in use.",
  email_taken: "Someone already has that email address.",
  forbidden: "You don't have access to that.",
  not_found: "That doesn't exist, or you can't see it.",
  last_passkey: "That's your only passkey — removing it would leave the account password-only.",
  cannot_demote_self: "You can't remove your own admin access.",
  not_awaiting_relay: "That meeting isn't waiting for a browser fetch.",
  stale_upload: "This fetch was restarted elsewhere, so this one stopped.",
  resolve_failed: "Couldn't read that recording's details from the video host.",
  not_deletable: "That meeting has moved past the point where it can be deleted.",
  not_editable: "Speakers can only be named once the transcript is ready, and before minutes are published.",
  unknown_label: "That voice isn't in this meeting's transcript.",
  person_not_on_roster: "That person isn't on this client's roster.",
  no_audio: "There's no audio stored for this meeting.",
  storage_delete_failed: "Couldn't remove the stored audio, so nothing was deleted. Try again.",
  audit_failed: "Couldn't record the deletion, so nothing was deleted. Try again.",
  weak_password: "Use at least 12 characters.",
  wrong_password: "Your current password isn't right.",
  link_invalid: "This link has expired or has already been used. Ask an administrator for a new one.",
  inactive: "That account is deactivated. Reactivate it first.",
  cannot_reset_own_passkeys: "You can't remove your own passkeys this way — do it from your Account page.",
  voiceprints_not_configured: "Voiceprints aren't switched on for this server.",
  nothing_to_enrol: "Everyone confirmed in this meeting already has a voice on file.",
  no_voiceprint: "There's no voiceprint on file for that person.",
  duplicate_seat: "A person can hold only one seat on each body.",
  staff_seat_needs_title: "A staff seat needs its title spelled out, e.g. \"Executive Director\".",
  body_not_on_client: "That body belongs to a different client.",
  abbrev_taken: "Another body already uses that abbreviation.",
  code_taken: "Another meeting type for this client already uses that code.",
  unusable_template: "That file can't be used as a template.",
  unreadable_examples: "Those files couldn't be read.",
  learn_failed: "Couldn't work out the style from those just now. Try again in a minute.",
  no_draft: "There is no draft of this meeting to compare with yet.",
  model_unavailable: "Style learning isn't switched on for this server.",
  no_saved_transcript: "There are no saved transcription results to rebuild from.",
  directory_blocked: "The city's website refuses automated requests. Open the page in your browser, copy the staff table and paste it instead.",
  directory_unreadable: "Couldn't find a staff list in that. Try copying just the staff table.",
  not_client_staff: "One of those people isn't on this client's staff.",
  duplicate_recording: "This recording was already submitted for this client.",
};

export class ApiError extends Error {
  constructor(public code: string, public status: number, public body: Record<string, unknown> = {}) {
    super(MESSAGES[code] ?? "Something went wrong. Please try again.");
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin" };
  // Only claim JSON when there is JSON. A content-type of application/json
  // with an empty body is rejected by Fastify with a 400 before auth even
  // runs -- which silently broke sign-out and every body-less POST.
  if (body !== undefined) {
    init.headers = { "content-type": "application/json" };
    init.body = JSON.stringify(body);
  }

  const res = await fetch(path, init);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(json?.error ?? `http_${res.status}`, res.status, json ?? {});
  return json as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, b?: unknown) => request<T>("POST", p, b),
  patch: <T>(p: string, b?: unknown) => request<T>("PATCH", p, b),
  put: <T>(p: string, b?: unknown) => request<T>("PUT", p, b),
  del: <T>(p: string) => request<T>("DELETE", p),
};

export function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) {
    if (err.name === "NotAllowedError") return "The passkey prompt was dismissed.";
    // fetch() rejects with a bare TypeError on network/CORS failure.
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      return "Couldn't reach the server or the video host. Check your connection and try again.";
    }
    // Our own thrown errors carry a specific, readable message. Flattening
    // them to a generic line is how a missing-header bug hid for a round.
    if (err.message) return err.message;
  }
  return "Something went wrong. Please try again.";
}

// ---------------------------------------------------------------- types

export interface Me {
  id: string; email: string; name: string; role: "admin" | "user";
}

export interface Client {
  id: string; name: string; slug: string; timezone: string;
  agendaPortalUrl: string | null; notes: string | null; active: boolean;
  staffDirectoryUrl: string | null;
}

export type SeatRole = "presiding" | "member" | "pro_tem" | "staff_to_body";
export interface Seat { body: string; bodyId: string; role: SeatRole; titleOverride: string | null; title: string }

export interface Person {
  id: string; firstName: string; lastName: string;
  kind: "official" | "staff" | "other";
  staffTitle: string | null; honorificPrefix: string | null;
  /** Set by the client; null means "what the honorific implies, else none". */
  pronoun: "he" | "she" | null;
  active: boolean;
  voiceprint: { status: "pending" | "ready" | "failed"; updatedAt: string } | null;
  seats: Seat[];
}

export interface MeetingTypeSummary {
  id: string; code: string; name: string;
  sections: number; hasFrame: boolean; promptNotesChars: number;
  filenamePattern: string; active: boolean; bodies: string[];
}

export interface Body {
  id: string; name: string; abbrev: string;
  presidingTitle: string; memberTitle: string; proTemTitle: string | null;
  sortOrder: number;
}

export interface ClientDetail extends Client {
  bodies: Body[];
  meetingTypes: MeetingTypeSummary[];
  people: Person[];
}

export interface Meeting {
  id: string; sourceUrl: string; platform: string | null;
  meetingDate: string | null; status: string; statusDetail: string | null;
  createdAt: string; clientId: string; clientName: string; clientTimezone: string;
  mediaBaseUrl: string | null; title: string | null;
  hasAudio: boolean; deletable: boolean;
  segments: { code: string; status: string }[];
}

export interface UserRow {
  id: string; email: string; name: string; role: "admin" | "user";
  active: boolean; mfaRequired: boolean;
  lastLoginAt: string | null; createdAt: string;
}

export interface Passkey {
  id: string; label: string; createdAt: string; lastUsedAt: string | null;
}
